import { useState, useEffect } from 'react';
import {
  ShieldCheckIcon,
  LockIcon,
  WalletIcon,
  CheckCircleIcon,
} from '../components/Icons.js';

const FREE_SCAN_LIMIT = 5;
const PRO_SCAN_LIMIT = 75;

const compareRows: { feature: string; free: string; pro: string }[] = [
  { feature: 'Monthly scans', free: String(FREE_SCAN_LIMIT), pro: String(PRO_SCAN_LIMIT) },
  { feature: 'Risk scoring', free: '\u2713', pro: '\u2713' },
  { feature: 'Sellability checks', free: '\u2014', pro: '\u2713' },
  { feature: 'Contract analysis', free: '\u2014', pro: '\u2713' },
  { feature: 'Real-time alerts', free: '\u2014', pro: '\u2713' },
  { feature: 'Account recovery tools', free: '\u2014', pro: '\u2713' },
  { feature: 'Support diagnostics', free: '\u2014', pro: '\u2713' },
];

const faqs = [
  {
    q: 'Does CryptoGuardians access my funds?',
    a: 'No. CryptoGuardians is completely non-custodial. We never have access to your private keys, seed phrase, or funds. We only analyze transaction data before you sign.',
  },
  {
    q: 'Do I need to create an account?',
    a: 'No traditional account is needed. Your MetaMask wallet address is your identity. Just connect your wallet and you\u2019re ready to go.',
  },
  {
    q: 'What counts as a "scan"?',
    a: 'A scan occurs each time CryptoGuardians analyzes a transaction or contract before you sign. Each unique analysis counts as one scan against your monthly limit.',
  },
  {
    q: 'What happens when I hit my scan limit?',
    a: 'You\u2019ll be notified when approaching your limit. On the Free plan, you can upgrade to Pro for more scans. Your limit resets at the start of each billing cycle.',
  },
  {
    q: 'Can I cancel anytime?',
    a: 'Yes. You can cancel your Pro subscription at any time from your dashboard. You\u2019ll keep Pro access until the end of your current billing period.',
  },
  {
    q: 'When will payments be enabled?',
    a: 'Payments are processed securely through Stripe. You can subscribe to Pro from the pricing page and manage your subscription from your Account Center.',
  },
];

export function Pricing() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => { document.title = 'CryptoGuardians Pricing \u2014 $9.99/mo Wallet Protection'; }, []);


  return (
    <div className="pricing-page">
      <div className="container pr-container">

        <section className="pr-hero">
          <h1>Simple pricing. Real wallet protection.</h1>
          <p className="pr-hero-subtitle">
            Protect your MetaMask transactions from scams, drainers, and malicious contracts &mdash; before you sign.
          </p>
          <div className="pr-hero-chips">
            <span className="pr-chip"><ShieldCheckIcon className="pr-chip-icon" aria-hidden="true" /> Non-custodial</span>
            <span className="pr-chip"><LockIcon className="pr-chip-icon" aria-hidden="true" /> No fund access</span>
          </div>
        </section>

        {/* ── Plan Cards ── */}
        <section className="pr-cards">
          <div className="pricing-grid">
            {/* Free */}
            <div className="dash-card pr-card">
              <h3 className="pr-card-plan">Free</h3>
              <div className="pr-card-price">
                $0<span className="pr-card-period">/month</span>
              </div>
              <p className="pr-card-best">Best for: Trying CryptoGuardians</p>
              <ul className="pr-card-features">
                <li><CheckCircleIcon className="pr-card-check" aria-hidden="true" />{FREE_SCAN_LIMIT} scans per month</li>
                <li><CheckCircleIcon className="pr-card-check" aria-hidden="true" />Basic risk scoring</li>
                <li><CheckCircleIcon className="pr-card-check" aria-hidden="true" />Works with MetaMask</li>
              </ul>
              <p className="pr-card-subtext">Available at launch</p>
            </div>

            {/* Pro */}
            <div className="dash-card pr-card pr-card-pro">
              <span className="pricing-badge">Launch Price</span>
              <h3 className="pr-card-plan">Pro</h3>
              <div className="pr-card-price">
                $9.99<span className="pr-card-period">/month</span>
              </div>
              <p className="pr-card-best">Best for: Active traders &amp; DeFi users</p>
              <ul className="pr-card-features">
                <li><CheckCircleIcon className="pr-card-check pr-card-check-pro" aria-hidden="true" />{PRO_SCAN_LIMIT} scans per month</li>
                <li><CheckCircleIcon className="pr-card-check pr-card-check-pro" aria-hidden="true" />Real-time alerts</li>
                <li><CheckCircleIcon className="pr-card-check pr-card-check-pro" aria-hidden="true" />Contract analysis</li>
                <li><CheckCircleIcon className="pr-card-check pr-card-check-pro" aria-hidden="true" />Sellability checks</li>
                <li><CheckCircleIcon className="pr-card-check pr-card-check-pro" aria-hidden="true" />Priority recovery tools</li>
              </ul>
              <a href="/connect" className="btn btn-primary btn-lg btn-primary-glow pr-card-cta">
                Get Started
              </a>
            </div>
          </div>
        </section>

        {/* ── Comparison Table ── */}
        <section className="pr-compare">
          <div className="dash-card">
            <h2 className="pr-section-title">Feature Comparison</h2>
            <div className="pr-compare-scroll">
              <table className="pr-compare-table">
                <thead>
                  <tr>
                    <th>Feature</th>
                    <th>Free</th>
                    <th className="pr-compare-highlight">Pro</th>
                  </tr>
                </thead>
                <tbody>
                  {compareRows.map((row) => (
                    <tr key={row.feature}>
                      <td>{row.feature}</td>
                      <td>{row.free}</td>
                      <td className="pr-compare-highlight">{row.pro}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* ── Trust Section ── */}
        <div className="dash-card">
          <div className="dash-card-header">
            <LockIcon className="dash-section-icon" aria-hidden="true" />
            <h2>Security &amp; Privacy</h2>
          </div>
          <div className="rc-trust-grid">
            <div className="rc-trust-item">
              <ShieldCheckIcon className="rc-trust-icon" aria-hidden="true" />
              <div>
                <h4>Non-Custodial</h4>
                <p>We never control your funds or private keys.</p>
              </div>
            </div>
            <div className="rc-trust-item">
              <LockIcon className="rc-trust-icon" aria-hidden="true" />
              <div>
                <h4>No Fund Access</h4>
                <p>CryptoGuardians cannot move, spend, or access your assets.</p>
              </div>
            </div>
            <div className="rc-trust-item">
              <WalletIcon className="rc-trust-icon" aria-hidden="true" />
              <div>
                <h4>No Transaction Signing</h4>
                <p>We analyze transactions &mdash; we never sign or execute them.</p>
              </div>
            </div>
          </div>
        </div>

        {/* ── FAQ ── */}
        <section className="pr-faq">
          <h2 className="pr-section-title">Frequently Asked Questions</h2>
          <div className="pr-faq-list">
            {faqs.map((faq, i) => (
              <div key={i} className={`pr-faq-item ${openFaq === i ? 'pr-faq-open' : ''}`}>
                <button
                  className="pr-faq-trigger"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  aria-expanded={openFaq === i}
                >
                  <span>{faq.q}</span>
                  <span className="pr-faq-chevron" aria-hidden="true">{openFaq === i ? '\u2212' : '+'}</span>
                </button>
                {openFaq === i && (
                  <div className="pr-faq-answer">
                    <p>{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>


      </div>
    </div>
  );
}
